import { Link } from "react-router-dom";
import { Sprout } from "lucide-react";

const Section = ({ eyebrow, title, children }) => (
  <section className="mb-14" data-testid={`privacy-section-${eyebrow.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-center gap-3 mb-4">
      <span className="w-2 h-2 rounded-full bg-orange-500" />
      <span className="eyebrow text-orange-500">{eyebrow}</span>
    </div>
    <h2 className="font-serif-display text-3xl md:text-4xl text-stone-100 mb-6 leading-tight">
      {title}
    </h2>
    <div className="text-stone-300 leading-relaxed text-lg space-y-4">{children}</div>
  </section>
);

const Privacy = () => (
  <div className="min-h-screen bg-stone-950 pt-32 pb-24">
    <div className="max-w-3xl mx-auto px-6 md:px-10" data-testid="privacy-page">
      <div className="flex items-baseline gap-6 mb-3">
        <span className="font-mono-accent text-orange-500 text-sm">/privacy</span>
        <span className="h-px w-24 bg-stone-800" />
        <span className="eyebrow text-stone-400">The Small Print</span>
      </div>
      <h1 className="font-serif-display text-4xl sm:text-5xl md:text-6xl text-stone-100 leading-[0.95] mb-4">
        Privacy, plainly said.
      </h1>
      <p className="text-stone-400 text-lg max-w-2xl mb-16">
        Cinder &amp; Sorrel is a cooking journal, not an ad network. We hold as little
        of your data as possible, and we tell you exactly what we do with it.
      </p>

      <Section eyebrow="Who we are" title="A tiny kitchen, publishing recipes">
        <p>
          Cinder &amp; Sorrel is an independent recipe project run out of the UK. If you'd
          like to reach us about anything on this page, contact the kitchen via the
          address in the site footer.
        </p>
      </Section>

      <Section eyebrow="What we collect" title="Only what the kitchen needs">
        <p>When you make an account we hold:</p>
        <ul className="list-disc list-outside ml-6 space-y-2">
          <li>Your <span className="text-stone-100">email address</span> and a
              <span className="text-stone-100"> hashed password</span> (we never see the plaintext).</li>
          <li>Your <span className="text-stone-100">favourites</span>, cookbooks, and any
              <span className="text-stone-100"> cook photos</span> you upload.</li>
          <li>Any <span className="text-stone-100">recipes you generate</span> via the AI generator, so we can rate-limit fairly.</li>
        </ul>
        <p>
          If you subscribe, <span className="text-stone-100">Stripe</span> handles your
          card details on their systems. We only store your Stripe customer and
          subscription IDs so we can honour your plan.
        </p>
        <p>
          We do <span className="text-stone-100">not</span> use analytics trackers,
          advertising cookies, or session replay tools. No pixels, no fingerprinting.
        </p>
      </Section>

      <Section eyebrow="How we use it" title="Serving you dinner, not adverts">
        <ul className="list-disc list-outside ml-6 space-y-2">
          <li>To let you sign in and see your saved recipes.</li>
          <li>To generate AI recipes and plating photos on demand (see below).</li>
          <li>To bill you fairly if you're on a paid plan.</li>
          <li>To moderate submitted cook photos before they appear on a recipe.</li>
        </ul>
        <p>We do not sell, rent, or share your data with third-party marketers. Ever.</p>
      </Section>

      <Section eyebrow="Third parties" title="Who else touches your data">
        <p>Three services help the kitchen run:</p>
        <ul className="list-disc list-outside ml-6 space-y-2">
          <li>
            <span className="text-stone-100">Stripe</span> — processes your subscription
            payments. Their privacy policy is at
            <a href="https://stripe.com/gb/privacy" target="_blank" rel="noreferrer" className="text-orange-400 hover:text-orange-300"> stripe.com/gb/privacy</a>.
          </li>
          <li>
            <span className="text-stone-100">Anthropic (Claude)</span> — writes recipe
            text when you use the AI generator. Your prompt (dish idea, region, season,
            focus) is sent to Anthropic through Emergent's key. Prompts are not
            tied to your account by Anthropic.
          </li>
          <li>
            <span className="text-stone-100">Google (Gemini "Nano Banana")</span> —
            generates the plated photograph for each recipe. Only the recipe's title and
            plating notes are sent — no personal data.
          </li>
        </ul>
      </Section>

      <Section eyebrow="Cookies" title="A shortbread crumb, nothing more">
        <p>
          We set one cookie: a session token so we know it's still you between page loads.
          It's marked HTTP-only and secure. We don't set marketing or analytics cookies.
        </p>
      </Section>

      <Section eyebrow="Your rights" title="You are in charge of your data">
        <p>Under UK &amp; EU GDPR you can:</p>
        <ul className="list-disc list-outside ml-6 space-y-2">
          <li>See what we hold on you.</li>
          <li>Correct anything wrong.</li>
          <li>Delete your account — this removes your favourites, cookbooks, cook photos, and Stripe customer link.</li>
          <li>Cancel your subscription any time from <Link to="/account" className="text-orange-400 hover:text-orange-300">/account</Link>.</li>
          <li>Take your data with you (email us and we'll export it).</li>
        </ul>
      </Section>

      <Section eyebrow="Retention" title="We keep it while you're using it">
        <p>
          Account data lives as long as your account. If you delete your account, your
          personal data is removed within 30 days. Anonymised recipe-usage counts may
          live on for capacity planning — no personal detail attached.
        </p>
      </Section>

      <Section eyebrow="Changes" title="If this page changes">
        <p>
          If we ever change how we handle your data, we'll update this page and,
          for anything material, email account holders in advance.
        </p>
      </Section>

      <div className="mt-20 pt-8 border-t border-stone-800 flex items-center gap-3 text-stone-500 text-sm">
        <Sprout className="w-4 h-4 text-lime-600" />
        <span>Last updated: February 2026</span>
      </div>
    </div>
  </div>
);

export default Privacy;
